import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretRight, MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import type { OverviewCampaign } from '../types/analytics-api.types';
import { fmtDate, fmtNum, fmtPct } from '../utils/report-format';

export const isSentOverviewCampaign = (status: string | null | undefined) =>
  (status ?? '').toLowerCase() === 'sent';

type ReportsCampaignsTableProps = {
  campaigns: OverviewCampaign[];
  days: number;
  complaintThreshold: number;
  deliveryDataAvailable: boolean;
};

export function ReportsCampaignsTable({
  campaigns,
  days,
  complaintThreshold,
  deliveryDataAvailable,
}: ReportsCampaignsTableProps) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const sent = useMemo(
    () => campaigns.filter((campaign) => isSentOverviewCampaign(campaign.status)),
    [campaigns]
  );

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return sent;
    return sent.filter((c) => c.name.toLowerCase().includes(needle));
  }, [sent, q]);

  return (
    <div className="rounded-lg border border-neutral-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 p-4">
        <div>
          <h3 className="text-base font-semibold text-neutral-900">
            Campaigns{' '}
            <span className="font-normal text-neutral-500">({fmtNum(sent.length)} in range)</span>
          </h3>
          <p className="mt-0.5 text-xs text-neutral-500">
            Open one for its performance, links and deliverability.
          </p>
        </div>
        <div className="flex min-w-[220px] items-center gap-2 rounded-md border border-neutral-300 bg-neutral-50 px-3 py-1.5 text-neutral-500 focus-within:border-primary-600">
          <MagnifyingGlass className="size-3.5 shrink-0" aria-hidden />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search campaigns"
            aria-label="Search campaigns in this table"
            className="h-auto border-0 bg-transparent p-0 text-xs text-neutral-900 shadow-none placeholder:text-neutral-500 focus-visible:ring-0"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                Campaign
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                Sent date
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Delivered
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Open rate
              </th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-neutral-500">
                Click rate
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-neutral-500">
                Complaint
              </th>
              <th className="w-8 px-4 py-2.5">
                <span className="sr-only">Open report</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && q.trim() ? (
              <tr>
                <td colSpan={7} className="px-6 py-14">
                  <div className="mx-auto flex max-w-[360px] flex-col items-center gap-3 text-center">
                    <MagnifyingGlass className="size-8 text-neutral-500" aria-hidden />
                    <div className="text-base font-semibold text-neutral-900">
                      No campaigns match
                    </div>
                    <p className="text-neutral-600">
                      Try a different search term, or clear it to see every sent campaign in range.
                    </p>
                    <Button type="button" className="gap-2" onClick={() => setQ('')}>
                      <XCircle className="size-4" aria-hidden />
                      Clear search
                    </Button>
                  </div>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-10 text-center text-sm text-neutral-600">
                  No sent campaigns in this range.
                </td>
              </tr>
            ) : (
              rows.map((c) => {
                const flagged = c.complaintRate != null && c.complaintRate >= complaintThreshold;
                return (
                  <tr
                    key={c.itemId}
                    className="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50"
                    onClick={() => navigate(`/reports/campaigns/${c.itemId}?days=${days}`)}
                  >
                    <td className="px-4 py-2.5 font-medium text-neutral-900">{c.name}</td>
                    <td className="px-4 py-2.5 text-neutral-600">{fmtDate(c.sentAt)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {deliveryDataAvailable ? fmtNum(c.delivered) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(c.openRate)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(c.clickRate)}</td>
                    <td className="px-4 py-2.5">
                      {flagged && c.complaintRate != null ? (
                        <span className="inline-flex rounded-full bg-[#FFF4E5] px-2 py-0.5 text-xs font-medium text-[#8A5B00]">
                          {fmtPct(c.complaintRate, 2)}
                        </span>
                      ) : c.complaintRate != null ? (
                        <span className="tabular-nums text-neutral-700">
                          {fmtPct(c.complaintRate, 2)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-neutral-400">
                      <CaretRight className="size-3.5" aria-hidden />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
